// Course card showing enrollment stats, pass rate, and last activity
import { ChevronRight, Award, TrendingUp, FileText } from 'lucide-react'

import { Progress } from '@/ui/progress'
import type { Course } from '@/features/classes/stores'

import type { CourseStats } from './CourseDetail'

interface CourseCardProps {
  course: Course
  stats: CourseStats
  onClick: () => void
}

export function CourseCard({ course, stats, onClick }: CourseCardProps) {
  return (
    <div
      onClick={onClick}
      className="bg-white border border-slate-150 rounded-xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
    >
      {/* Color accent band */}
      <div className="h-1.5 bg-gradient-to-r from-navy to-primary" />

      <div className="p-5 space-y-4">
        {/* Title */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-navy group-hover:text-primary transition-colors">{course.name}</h3>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1 group-hover:text-primary transition-colors" />
        </div>

        {/* Enrollment breakdown */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-base font-bold text-navy">{stats.enrolled}</p>
            <p className="text-xs text-slate-500">Inscritos</p>
          </div>
          <div className="bg-green-50 rounded-lg p-2">
            <p className="text-base font-bold text-green-600">{stats.completed}</p>
            <p className="text-xs text-slate-500">Completados</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-2">
            <p className="text-base font-bold text-primary">{stats.inProgress}</p>
            <p className="text-xs text-slate-500">En proceso</p>
          </div>
        </div>

        {/* Pass rate bar */}
        <div>
          <div className="flex justify-between mb-1 text-xs text-slate-500">
            <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Tasa de aprobación</span>
            <span className="font-semibold text-navy">{stats.passRate !== null ? `${stats.passRate}%` : '—'}</span>
          </div>
          <Progress value={stats.passRate ?? 0} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-0.5">
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" /> {stats.certificatesIssued} certificados emitidos
          </span>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Award className="w-3.5 h-3.5" /> {course.validYears} año{course.validYears > 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>
  )
}
